import * as React from 'react';

type TErrorBoundaryProps = {
  children: React.ReactNode;
  /** Optional custom fallback UI */
  fallback?: React.ReactNode;
  /** Called when an error is caught (e.g. send to Sentry) */
  onError?: (error: Error, info: React.ErrorInfo) => void;
  /** Enable a minimal "Try again" reset button */
  allowReset?: boolean;
};

type TErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  info?: React.ErrorInfo;
  resetKey: number;
};

const isDev =
  typeof process !== 'undefined' &&
  process.env.NODE_ENV !== 'production';

export class ErrorBoundary extends React.Component<
  TErrorBoundaryProps,
  TErrorBoundaryState
> {
  state: TErrorBoundaryState = {
    hasError: false,
    error: undefined,
    info: undefined,
    resetKey: 0,
  };

  static getDerivedStateFromError(
    error: Error
  ): Partial<TErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ info });
    this.props.onError?.(error, info);
  }

  private reset = () => {
    this.setState((s) => ({
      hasError: false,
      error: undefined,
      info: undefined,
      resetKey: s.resetKey + 1,
    }));
  };

  render() {
    const {
      children,
      fallback,
      allowReset = true,
    } = this.props;
    const { hasError, error, info, resetKey } = this.state;

    if (!hasError) {
      // resetKey forces subtree remount after reset
      return (
        <React.Fragment key={resetKey}>
          {children}
        </React.Fragment>
      );
    }

    if (fallback) return <>{fallback}</>;

    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl items-center justify-center p-6">
        <div className="w-full rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
          <div className="flex items-start gap-3">
            <div className="mt-1 h-2.5 w-2.5 rounded-full bg-red-500" />
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                Something went wrong
              </h1>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                Please try again. If it keeps happening,
                refresh the page.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {allowReset && (
                  <button
                    type="button"
                    onClick={this.reset}
                    className="rounded-xl bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:opacity-90 dark:bg-zinc-100 dark:text-zinc-900"
                  >
                    Try again
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-100 dark:hover:bg-zinc-900"
                >
                  Refresh
                </button>
              </div>

              {isDev && (
                <details className="mt-5 rounded-xl border border-zinc-200 bg-zinc-50 p-4 text-sm dark:border-zinc-800 dark:bg-zinc-900/40">
                  <summary className="cursor-pointer select-none font-medium text-zinc-900 dark:text-zinc-100">
                    Error details (dev)
                  </summary>
                  <pre className="mt-3 overflow-auto whitespace-pre-wrap break-words text-xs text-zinc-800 dark:text-zinc-200">
                    {String(
                      error?.stack ||
                        error?.message ||
                        error ||
                        'Unknown error'
                    )}
                    {info?.componentStack
                      ? `\n\nComponent stack:\n${info.componentStack}`
                      : ''}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
